import React from "react";
import { useSelector } from "react-redux";
import { isEmptyArray, useFormik } from "formik";
import { isEmpty } from "lodash/fp";
import { Button, Grid, IconButton } from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { selectCurrentFile, selectCurrentFileId } from "../../../store/fileStore/fileSelectors";
import HorizontalPanel from "../../../components/HorizontalPanel";
import { Toolbar } from "../../../app/components";
import { FileVisibilityLevel, VisibilityDropdownValues } from "./fileVisibilityLevel";
import { Dropdown } from "../../../app/components/Dropdown";
import SelectFile, { FakeFile } from "../../../components/SelectFile";
import { selectAllCountriesSorted } from "../../../store/countryStore/countrySelectors";
import { selectAllLocationsSorted } from "../../../store/locationStore/locationSelectors";
import { selectAllBuildingsSorted } from "../../../store/buildingStore/buildingsSelectors";
import Multiselect from "../../../components/Multiselect";
import {
  deleteFile,
  editFile, EditFileInterface,
  getFilesByQuery,
  uploadFile,
  UploadFileInterface
} from "../../../store/fileStore/fileRequests";
import { useTypedDispatch } from "../../../app/state";
import { clearCurrentFiles, setCurrentFileId } from "../../../store/fileStore/fileState";
import fileValidationSchema from "./validationSchema";
import { extractFilesData } from "../filesHelper";
import { Delete } from "@material-ui/icons";
import { ModalType } from "../../../app/shared/modal-context/constants";
import { useShowModal } from "../../../app/shared/modal-context/modal.context";
import FileModel from "../../../models/FileModel";

import classes from './index.module.scss';
import { useSnackbar } from "../../../app/shared/snackbar-context/snackbar.context";

interface FileEditProps {
  id?: number,
  visibility: FileVisibilityLevel,
  file?: File,
  country?: number,
  locations: number[],
  buildings: number[],
}

const EditFile = () => {
  const dispatch = useTypedDispatch();
  const { showModal } = useShowModal();
  const { openSnackbar } = useSnackbar();
  const currentFile = useSelector(selectCurrentFile);
  const currentFileId = useSelector(selectCurrentFileId);
  const countryList = useSelector(selectAllCountriesSorted) || [];
  const locationList = useSelector(selectAllLocationsSorted) || [];
  const buildingsList = useSelector(selectAllBuildingsSorted) || [];
  const isCreate = currentFileId === -1;
  const isEdit = !!currentFile;
  const title = isCreate ? 'Upload a new file' : isEdit ? 'Edit file' : '';
  const fakeFile: FakeFile | null = isEdit ? {
    name: currentFile?.name || '',
    size: currentFile?.size || 0,
  } : null;
  const fileData = extractFilesData(currentFile, countryList, locationList);

  const initialValues: FileEditProps = isCreate ? {
    visibility: FileVisibilityLevel.LOCATION,
    locations: [],
    buildings: [],
  } : {
    visibility: isEmptyArray(currentFile?.fileLink?.buildings || []) ? FileVisibilityLevel.LOCATION : FileVisibilityLevel.BUILDING,
    country: fileData?.countryId,
    locations: fileData?.locationId ? [ fileData?.locationId ] : (currentFile?.fileLink?.locations || []).map((location) => location.id),
    buildings: (currentFile?.fileLink?.buildings || []).map((building) => building.id),
  };

  const fileEditFormik = useFormik({
    initialValues,
    onSubmit: () => {},
    validationSchema: fileValidationSchema(isCreate),
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const filteredLocationList = locationList.filter((location) => location.countryId === fileEditFormik.values.country);
  const selectLocationList = Multiselect.getValues(filteredLocationList);
  const currentLocationList = Multiselect.getSelectedValues(filteredLocationList, fileEditFormik.values.locations);

  const filteredBuildingsList = buildingsList.filter((building) => building.locationId === fileEditFormik.values.locations[0]);
  const selectBuildingsList = Multiselect.getValues(filteredBuildingsList);
  const currentBuildingsList = Multiselect.getSelectedValues(filteredBuildingsList, fileEditFormik.values.buildings);

  const handleFile = (file: File) => {
    fileEditFormik.setFieldValue('file', file);
  }

  const handleVisibility = (e) => {
    fileEditFormik.handleChange(e);
    fileEditFormik.setFieldValue('locations', []);
    fileEditFormik.setFieldValue('buildings', []);
  }

  const onCountryChange = (e) => {
    fileEditFormik.handleChange(e);
    fileEditFormik.setFieldValue('locations', []);
    fileEditFormik.setFieldValue('buildings', []);
  }

  const onLocationChange = (e) => {
    fileEditFormik.setFieldValue('locations', [ e.target.value ]);
    fileEditFormik.setFieldValue('buildings', []);
  }

  const onChangeLocationsMultiselect = (values) => {
    fileEditFormik.setFieldValue('locations', values);
  }

  const onBuildingChangeMultiselect = (values) => {
    fileEditFormik.setFieldValue('buildings', values);
  }

  const resetFiles = (id: number | null = null) => {
    fileEditFormik.resetForm();
    dispatch(clearCurrentFiles());
    dispatch(getFilesByQuery({}));
    dispatch(setCurrentFileId(id));
  }

  const onUploadFile = () => {
    if (fileEditFormik.values.file) {
      const payload: UploadFileInterface = {
        file: fileEditFormik.values.file,
        locationId: null,
        buildingId: null,
      };

      if (fileEditFormik.values.visibility === FileVisibilityLevel.LOCATION) {
        payload.locationId = fileEditFormik.values.locations;
      } else {
        payload.buildingId = fileEditFormik.values.buildings;
      }

      openSnackbar({
        message: 'Upload file has been started',
        title: 'Uploading',
        severity: "info",
      });

      dispatch(uploadFile(payload))
        .then((action) => {
          // @ts-ignore
          if (action?.error) {
            openSnackbar({
              message: 'There was an error uploading your file. Please, try again later',
              title: 'Upload failed',
              severity: "error",
            });

            return;
          }
          openSnackbar({
            message: 'File has been successfully uploaded',
            title: 'Uploaded',
            severity: "success",
          });
          const fileModel = action.payload as FileModel;
          resetFiles(fileModel.id);
        })
    }
  }

  const onEditFile = () => {
    if (!currentFileId) {
      return;
    }

    const payload: EditFileInterface = {
      id: currentFileId,
    };

    if (fileEditFormik.values.visibility === FileVisibilityLevel.LOCATION) {
      payload.locations = fileEditFormik.values.locations;
    } else {
      payload.buildings = fileEditFormik.values.buildings;
    }

    dispatch(editFile(payload))
      .then(() => {
        resetFiles(currentFileId);
      })
  }

  const onSaveChanges = async () => {
    const errors = await fileEditFormik.validateForm();
    if (isEmpty(errors)) {
      if (isCreate) {
        onUploadFile();
      } else {
        onEditFile();
      }
    }
  }


  const onContinueDelete = () => {
    if (!currentFileId) {
      return;
    }

    dispatch(deleteFile(currentFileId))
      .then(() => resetFiles(null))
  }

  const onDeleteClick = () => {
    showModal(ModalType.MODAL_GENERIC_CONFIRM, {
      title: "Delete file",
      text: "Do you really want to delete this file?",
      confirmButtonHandler: onContinueDelete,
      confirmButtonText: "Delete",
    });
  }

  if (!title) {
    return null;
  }

  return (
    <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title={title}>
          <IconButton
            disabled={!isEdit}
            aria-label='remove file'
            color='secondary'
            size='small'
            onClick={onDeleteClick}
          >
            <Delete />
          </IconButton>
          <IconButton
            aria-label='save changes'
            color='secondary'
            size='small'
            disabled={!fileEditFormik.dirty}
            onClick={onSaveChanges}
          >
            <SaveIcon />
          </IconButton>
        </Toolbar>
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.REST_SPACE} addScroll className={classes.scrollPanel}>
        <Grid
          container
          spacing={2}
        >
          <Grid item xs={6}>
            <SelectFile
              handleFile={handleFile}
              accept="*.*"
              label="upload file"
              disabled={isEdit}
              selectedFile={fakeFile || fileEditFormik.values.file}
              error={fileEditFormik.errors.file}
              trigger={(
                <Button variant="contained" color="primary" type="submit" fullWidth>Choose file</Button>
              )}
            />
          </Grid>
          <Grid item xs={6}>&nbsp;</Grid>
          <Grid item xs={6}>
            <Dropdown
              selectedValue={fileEditFormik.values.visibility}
              values={VisibilityDropdownValues}
              label='Visibility Level'
              name='visibility'
              required
              valueChange={handleVisibility}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>&nbsp;</Grid>
          <Grid item xs={6}>
            <Dropdown
              selectedValue={fileEditFormik.values.country}
              values={countryList}
              label='Country'
              name='country'
              valueChange={onCountryChange}
              fullWidth
              required
              error={fileEditFormik.errors.country}
            />
          </Grid>
          <Grid item xs={6}>&nbsp;</Grid>
          {
            fileEditFormik.values.visibility === FileVisibilityLevel.LOCATION ? (
              <Grid item xs={6}>
                <Multiselect
                  title='Locations'
                  name='locations'
                  disabled={filteredLocationList.length === 0}
                  fullWidth
                  required
                  items={selectLocationList}
                  selectedItems={currentLocationList}
                  onChange={onChangeLocationsMultiselect}
                  helperText={fileEditFormik.errors.locations}
                  error={Boolean(fileEditFormik.errors.locations)}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={6}>
                  <Dropdown
                    selectedValue={fileEditFormik.values.locations[0]}
                    values={filteredLocationList}
                    disabled={filteredLocationList.length === 0}
                    label='Location'
                    name='locations'
                    valueChange={onLocationChange}
                    fullWidth
                    required
                    error={fileEditFormik.errors.locations}
                  />
                </Grid>
                <Grid item xs={6}>&nbsp;</Grid>
                <Grid item xs={6}>
                  <Multiselect
                    title='Buildings'
                    name='buildings'
                    disabled={selectBuildingsList.length === 0}
                    fullWidth
                    required
                    items={selectBuildingsList}
                    selectedItems={currentBuildingsList}
                    onChange={onBuildingChangeMultiselect}
                    helperText={fileEditFormik.errors.buildings}
                    error={Boolean(fileEditFormik.errors.buildings)}
                  />
                </Grid>
              </>
            )
          }
        </Grid>
      </HorizontalPanel>
    </HorizontalPanel>
  )
}

export default EditFile;